import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './SolveExam.module.css';
import apiClient from '../../../api/client';

const formatCountdown = (ms) => {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600).toString().padStart(2, '0');
  const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return days > 0 ? `${days} يوم ${hours}:${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
};

const SolveExam = () => {
  const { examId } = useParams();

  const [exam, setExam] = useState(null);
  const [examStatus, setExamStatus] = useState(null); // 'not_open' | 'closed' | 'open'
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [studentCode, setStudentCode] = useState('');
  const [started, setStarted] = useState(false);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState('');

  // مؤقت العد التنازلي قبل ما الامتحان يفتح
  const [msUntilOpen, setMsUntilOpen] = useState(0);

  const loadExam = React.useCallback(() => {
    apiClient.get(`/exams/${examId}/take`)
      .then(res => {
        setExam(res.data);
        setExamStatus(res.data.status || 'open');
        setTimeLeft((res.data.duration || 60) * 60);
        if (res.data.status === 'not_open' && res.data.availableFrom) {
          setMsUntilOpen(new Date(res.data.availableFrom).getTime() - Date.now());
        }
        setLoading(false);
      })
      .catch(() => {
        setLoadError('الامتحان ده مش موجود أو الرابط غلط، تأكد منه مع المدرس');
        setLoading(false);
      });
  }, [examId]);

  // جلب بيانات الامتحان (بدون الإجابات الصحيحة)
  useEffect(() => {
    loadExam();
  }, [loadExam]);

  // العداد التنازلي قبل فتح الامتحان — يعيد التحقق تلقائي أول ما الوقت يخلص
  useEffect(() => {
    if (examStatus !== 'not_open') return;
    if (msUntilOpen <= 0) {
      loadExam(); // اتفتح الوقت، هنعيد الجلب عشان نتأكد ونفتح الامتحان
      return;
    }
    const timer = setInterval(() => {
      setMsUntilOpen(prev => {
        if (prev <= 1000) {
          loadExam();
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [examStatus, msUntilOpen, loadExam]);

  const handleSubmit = React.useCallback(async () => {
    if (submitting || result) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await apiClient.post(`/exams/${examId}/submit`, {
        studentCode,
        answers,
      });
      setResult(data);
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'حصل خطأ أثناء تسليم الامتحان');
    } finally {
      setSubmitting(false);
    }
  }, [examId, studentCode, answers, submitting, result]);

  // العداد التنازلي لمدة الامتحان — يسلّم تلقائي لما الوقت يخلص
  useEffect(() => {
    if (!started || result) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [started, timeLeft, result, handleSubmit]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleSelectOption = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  if (loading) return <div className={styles.examWrapper}>جاري تحميل الامتحان...</div>;
  if (loadError) return <div className={styles.examWrapper}>❌ {loadError}</div>;

  // شاشة "الامتحان لسه مفتحش" مع عداد تنازلي لميعاد الفتح
  if (examStatus === 'not_open') {
    return (
      <div className={styles.examWrapper}>
        <div className={styles.questionCard} style={{ textAlign: 'center' }}>
          <h2 className={styles.questionText}>{exam.name}</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>الامتحان لسه معتقفلش، استنى الميعاد ده:</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '1rem', color: '#10b981' }}>
            ⏳ {formatCountdown(msUntilOpen)}
          </p>
        </div>
      </div>
    );
  }

  // شاشة "انتهى وقت الامتحان"
  if (examStatus === 'closed') {
    return (
      <div className={styles.examWrapper}>
        <div className={styles.questionCard} style={{ textAlign: 'center' }}>
          <h2 className={styles.questionText}>❌ انتهى وقت الامتحان</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            معلش، ميعاد امتحان "{exam.name}" خلص. تواصل مع المدرس لو فيه استفسار.
          </p>
        </div>
      </div>
    );
  }

  // شاشة إدخال كود الطالب قبل البدء
  if (!started) {
    return (
      <div className={styles.examWrapper}>
        <div className={styles.questionCard}>
          <h2 className={styles.questionText}>{exam.name}</h2>
          <p>عدد الأسئلة: {exam.questions?.length || 0} — المدة: {exam.duration} دقيقة</p>
          <div className={styles.formGroup || ''} style={{ marginTop: '1rem' }}>
            <label>اكتب كود الطالب بتاعك عشان تبدأ:</label>
            <input
              type="text"
              value={studentCode}
              onChange={(e) => setStudentCode(e.target.value)}
              placeholder="مثال: 1001"
              style={{ width: '100%', padding: '0.75rem', marginTop: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </div>
          <button
            className={`${styles.navBtn} ${styles.submitBtn}`}
            style={{ marginTop: '1rem' }}
            disabled={!studentCode.trim()}
            onClick={() => setStarted(true)}
          >
            ابدأ الامتحان
          </button>
        </div>
      </div>
    );
  }

  // شاشة النتيجة بعد التسليم
  if (result) {
    return (
      <div className={styles.examWrapper}>
        <div className={styles.questionCard} style={{ textAlign: 'center' }}>
          <h2 className={styles.questionText}>تم تسليم الامتحان بنجاح! 🎉</h2>
          <p style={{ fontSize: '1.5rem', marginTop: '1rem' }}>
            درجتك: <strong>{result.score}</strong> من {result.maxScore}
          </p>
          <p style={{ color: '#6b7280' }}>({result.correctCount} إجابة صحيحة من {result.total})</p>
        </div>
      </div>
    );
  }

  const currentQuestion = exam.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === exam.questions.length - 1;

  return (
    <div className={styles.examWrapper}>
      {/* الهيدر والعداد */}
      <div className={styles.examHeader}>
        <div className={styles.examTitle}>{exam.name}</div>
        <div className={styles.timer}>
          ⏱️ {formatTime(timeLeft)}
        </div>
      </div>

      {submitError && (
        <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem' }}>
          {submitError}
        </div>
      )}

      {/* كارت السؤال */}
      <div className={styles.questionCard}>
        <div className={styles.questionProgress}>
          سؤال {currentQuestionIndex + 1} من {exam.questions.length}
        </div>

        <h2 className={styles.questionText}>{currentQuestion.text}</h2>

        <div className={styles.optionsGrid}>
          {currentQuestion.options.map((option, index) => (
            <label
              key={index}
              className={`${styles.optionLabel} ${answers[currentQuestion._id] === index ? styles.selected : ''}`}
            >
              <input
                type="radio"
                name={`question-${currentQuestion._id}`}
                checked={answers[currentQuestion._id] === index}
                onChange={() => handleSelectOption(currentQuestion._id, index)}
                className={styles.optionInput}
              />
              {option}
            </label>
          ))}
        </div>

        {/* زراير التحكم (السابق، التالي، تسليم) */}
        <div className={styles.navigationBtns}>
          <button
            className={`${styles.navBtn} ${styles.prevBtn}`}
            disabled={currentQuestionIndex === 0}
            onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
            style={{ opacity: currentQuestionIndex === 0 ? 0.5 : 1 }}
          >
            السؤال السابق
          </button>

          {isLastQuestion ? (
            <button
              className={`${styles.navBtn} ${styles.submitBtn}`}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'جاري التسليم...' : 'تسليم الامتحان'}
            </button>
          ) : (
            <button
              className={`${styles.navBtn} ${styles.nextBtn}`}
              onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
            >
              السؤال التالي
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolveExam;
